import * as ImagePicker from "expo-image-picker";
import {forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState} from "react";

import type {
    Attachment,
    AttachmentWithFileOrNativeFile,
    FileType,
    NativeImageType
} from "../../models/attachment";

import Dropzone from "./Dropzone";
import ImageList from "./ImageList";
import type {UploadImageAreaProps} from "./types";
import {
    filterAttachmentOrFileType,
    filterAttachmentOrNativeImage,
    isAttachment,
    isFileType,
    isNativeImage
} from "./util";

type UploadImageRef = {
    onUploadProcess(file: FileType | NativeImageType, progress: number): void;
    setAttachmentOrFileList: (attachmentOrFileList: AttachmentWithFileOrNativeFile[]) => void;
};

type Props = UploadImageAreaProps;

const UploadImageArea = forwardRef<UploadImageRef, Props>(
    (
        {
            className,
            attachments: initAttachments,
            maxImages = 5,
            uploading,
            tagOptions,
            canReorder,
            noDrag,
            showDropzoneOverlay,
            size = "md",
            isNative,
            onDeleteAttachment,
            onRemoveImageFile,
            onChangeAttachmentOrFileList,
            children,
            onError
        },
        ref
    ) => {
        const [attachmentOrFileList, setAttachmentOrFileList] = useState<
            AttachmentWithFileOrNativeFile[]
        >(initAttachments ?? []);
        const numOfImagesLeft = maxImages - attachmentOrFileList.length;

        // still allow to set the images again in case no edits yet
        useEffect(() => {
            setAttachmentOrFileList(prev => {
                if (initAttachments?.length) {
                    const hasNewFiles = prev.some(elem => {
                        if (isNative) {
                            return isNativeImage(elem);
                        }
                        return isFileType(elem);
                    });
                    if (!hasNewFiles) {
                        return initAttachments;
                    }
                }
                return prev;
            });
        }, [initAttachments, isNative]);

        const handleDeleteAttachment = useCallback(
            (url: string) => {
                setAttachmentOrFileList(prev => {
                    const result = prev.find(
                        (elem): elem is Attachment => isAttachment(elem) && elem.url === url
                    );
                    if (result) {
                        onDeleteAttachment?.(result);
                    }
                    return prev.filter(elem => !isAttachment(elem) || elem.url !== url);
                });
            },
            [onDeleteAttachment]
        );

        const handleRemoveImage = useCallback(
            (image: File | NativeImageType) => {
                setAttachmentOrFileList(prev => {
                    const next = prev.filter(elem => {
                        if (isFileType(elem) && "name" in image) {
                            return elem.file.name !== image.name;
                        }
                        if (isNativeImage(elem) && "uri" in image) {
                            return elem.uri !== image.uri;
                        }
                        return true;
                    });

                    if (isNative) {
                        onChangeAttachmentOrFileList?.(filterAttachmentOrNativeImage(next));
                    } else {
                        onChangeAttachmentOrFileList?.(filterAttachmentOrFileType(next));
                    }
                    return next.length ? next : [];
                });

                if (isNative) {
                    if (isNativeImage(image)) {
                        onRemoveImageFile?.(image);
                    }
                } else if (!isNativeImage(image)) {
                    onRemoveImageFile?.(image);
                }
            },
            [isNative, onChangeAttachmentOrFileList, onRemoveImageFile]
        );

        const handleResizeImage = useCallback(
            (image: {name: string; blob: Blob}) => {
                setAttachmentOrFileList(prev => {
                    if (prev && !isNative) {
                        const idx = prev.findIndex(
                            elem => isFileType(elem) && elem.file.name === image.name
                        );
                        const newFiles = Object.assign([...prev], {
                            [idx]: {...prev[idx], resize: image.blob}
                        });
                        onChangeAttachmentOrFileList?.(filterAttachmentOrFileType(newFiles));
                        return newFiles;
                    }
                    return prev;
                });
            },
            [isNative, onChangeAttachmentOrFileList]
        );

        const handleChangeTags = useCallback(
            (file: File | string, tags: string[]) => {
                setAttachmentOrFileList(prev => {
                    let idx: number | undefined;
                    if (typeof file === "string") {
                        idx = prev.findIndex(elem => isAttachment(elem) && elem.url === file);
                    } else {
                        idx = prev.findIndex(
                            elem => isFileType(elem) && elem.file.name === file.name
                        );
                    }
                    const newFiles = Object.assign([...prev], {
                        [idx]: {...prev[idx], tags}
                    });
                    if (isNative) {
                        onChangeAttachmentOrFileList?.(filterAttachmentOrNativeImage(newFiles));
                    } else {
                        onChangeAttachmentOrFileList?.(filterAttachmentOrFileType(newFiles));
                    }
                    return newFiles;
                });
            },
            [isNative, onChangeAttachmentOrFileList]
        );

        const handleOpenNativeImagePicker = useCallback(async () => {
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ["images"],
                quality: 0.7,
                base64: true,
                allowsMultipleSelection: true,
                selectionLimit: numOfImagesLeft
                // allowsEditing: true,
                // aspect: [4, 3],
            });

            if (!result.canceled) {
                setAttachmentOrFileList(prev => {
                    const newFiles = [...prev, ...result.assets];
                    if (isNative) {
                        onChangeAttachmentOrFileList?.(filterAttachmentOrNativeImage(newFiles));
                    }
                    return newFiles;
                });
            }
        }, [isNative, numOfImagesLeft, onChangeAttachmentOrFileList]);

        useImperativeHandle(
            ref,
            () => ({
                onUploadProcess(file, progress) {
                    setAttachmentOrFileList(prev => {
                        const idx = prev.findIndex(elem => {
                            if (isFileType(elem) && isFileType(file)) {
                                return elem.file.name === file.file.name;
                            }
                            if (isNativeImage(elem) && isNativeImage(file)) {
                                return elem.uri === file.uri;
                            }
                            return false;
                        });
                        const newFiles = Object.assign([...prev], {
                            [idx]: {...prev[idx], progress}
                        });
                        if (isNative) {
                            onChangeAttachmentOrFileList?.(filterAttachmentOrNativeImage(newFiles));
                        } else {
                            onChangeAttachmentOrFileList?.(filterAttachmentOrFileType(newFiles));
                        }
                        return newFiles;
                    });
                },
                setAttachmentOrFileList
            }),
            [isNative, onChangeAttachmentOrFileList]
        );

        const onSortEnd = useCallback(
            (result: AttachmentWithFileOrNativeFile[]) => {
                setAttachmentOrFileList(result);
                if (isNative) {
                    onChangeAttachmentOrFileList?.(filterAttachmentOrNativeImage(result));
                } else {
                    onChangeAttachmentOrFileList?.(filterAttachmentOrFileType(result));
                }
            },
            [isNative, onChangeAttachmentOrFileList]
        );

        const handleDrop = useCallback(
            (acceptedFiles: File[]) => {
                if (acceptedFiles.length > numOfImagesLeft) {
                    onError?.("maxImages");
                    return;
                }
                setAttachmentOrFileList(prev => {
                    const next = [
                        ...prev,
                        ...acceptedFiles.reduce<FileType[]>((result, file) => {
                            if (
                                prev.find(elem => isFileType(elem) && elem.file.name === file.name)
                            ) {
                                return result;
                            }
                            result.push({file, progress: 0});
                            return result;
                        }, [])
                    ];
                    return next.splice(0, maxImages);
                });
            },
            [maxImages, numOfImagesLeft, onError]
        );

        if (process.env.IS_WEB && !isNative) {
            return (
                <Dropzone
                    className={className}
                    maxImages={numOfImagesLeft}
                    noDrag={noDrag}
                    showDropzoneOverlay={showDropzoneOverlay}
                    onDrop={handleDrop}
                >
                    {({open, isDragAccept}) =>
                        children ? (
                            children({
                                attachmentOrFileList:
                                    filterAttachmentOrFileType(attachmentOrFileList),
                                onRemoveFile: handleRemoveImage,
                                onOpen: open,
                                onResize: handleResizeImage
                            })
                        ) : (
                            <ImageList
                                attachments={filterAttachmentOrFileType(attachmentOrFileList)}
                                canReorder={canReorder}
                                isDragAccept={isDragAccept}
                                isNative={false}
                                maxImages={maxImages}
                                size={size}
                                tagOptions={tagOptions}
                                uploading={uploading}
                                onChangeTags={handleChangeTags}
                                onOpen={open}
                                onRemoveAttachment={handleDeleteAttachment}
                                onRemoveImageFile={handleRemoveImage}
                                onResizeImage={handleResizeImage}
                                onSortEnd={onSortEnd}
                            />
                        )
                    }
                </Dropzone>
            );
        }

        return (
            <ImageList
                isNative
                attachments={filterAttachmentOrNativeImage(attachmentOrFileList)}
                maxImages={maxImages}
                size={size}
                tagOptions={tagOptions}
                uploading={uploading}
                onChangeTags={handleChangeTags}
                onOpen={handleOpenNativeImagePicker}
                onRemoveAttachment={handleDeleteAttachment}
                onRemoveImageFile={handleRemoveImage}
            />
        );
    }
);

UploadImageArea.displayName = "UploadImageArea";

export default memo(UploadImageArea);
